// Receiving screen helpers: which POs can be received here, the count in progress (kept in this
// browser until it's posted, so a reload or a dropped connection loses nothing), and scan feedback.
import { useMemo } from "react";
import type { Item, Po, PoLine, Receipt } from "../../../../shared/db.gen.ts";
import { backersLeft, canReceive, hhlDone, type RejectReason } from "../../../../shared/receiving.ts";
import { useRows } from "../../lib/sync.ts";

export type LineCount = { good: number; bad: number; reason: RejectReason; photo: string; notes: string; seen: number | null };
export type Draft = { id: string; counts: Record<string, LineCount>; notes: string; closes: boolean; started: string };

export const emptyCount = (seen: number | null): LineCount => ({ good: 0, bad: 0, reason: "damaged", photo: "", notes: "", seen });

const key = (poId: string) => `hhl-receiving:${poId}`;

export function loadDraft(poId: string): Draft {
  try {
    const saved = JSON.parse(localStorage.getItem(key(poId)) ?? "null") as Draft | null;
    if (saved?.id) return saved;
  } catch {}
  // The receipt's id is made now, so posting it twice (a double tap, a retry after a timeout) records it once.
  return { id: crypto.randomUUID(), counts: {}, notes: "", closes: false, started: new Date().toISOString() };
}

export function saveDraft(poId: string, draft: Draft) {
  try {
    const counted = Object.values(draft.counts).some((c) => c.good || c.bad || c.notes || c.photo);
    if (counted || draft.notes || draft.closes) localStorage.setItem(key(poId), JSON.stringify(draft));
    else localStorage.removeItem(key(poId));
  } catch {}
}

export function clearDraft(poId: string) {
  try {
    localStorage.removeItem(key(poId));
  } catch {}
}

export const hasDraft = (poId: string) => {
  try {
    return localStorage.getItem(key(poId)) !== null;
  } catch {
    return false;
  }
};

/** A PO line as receiving reads it: Pre-sales, with its PO's "nothing else is coming to HHL" and what's built. */
export type ReceivingLine = PoLine & { hhl_closed: boolean; qty_output: number | null };

/**
 * POs a delivery to HHL can be counted against, soonest expected first; plus their lines and items. Pre-sales --- a make
 * PO whose HHL share is all in (or closed) and that only waits for its backers' units at the CM isn't expected here:
 * it's in `forBackers` ("at Tailhoo for backers") until they're built or shipped.
 */
export function useReceiving() {
  const pos = useRows("po");
  const lines = useRows("po_line");
  const items = useRows("item");
  const locations = useRows("location");
  const vendors = useRows("vendor");
  const receipts = useRows("receipt");
  const makeOrders = useRows("make_order");
  return useMemo(() => {
    const vendorSites = new Set(locations.filter((l) => l.kind !== "hhl").map((l) => l.id));
    const poById = new Map(pos.map((p) => [p.id, p]));
    const output = new Map<string, number>();
    for (const m of makeOrders) output.set(m.po_line_id, (output.get(m.po_line_id) ?? 0) + Number(m.qty_output));
    const linesByPo = new Map<string, ReceivingLine[]>();
    for (const l of lines)
      linesByPo.set(l.po_id, [
        ...(linesByPo.get(l.po_id) ?? []),
        { ...l, hhl_closed: !!poById.get(l.po_id)?.hhl_share_closed, qty_output: output.get(l.id) ?? null },
      ]);
    for (const list of linesByPo.values()) list.sort((a, b) => a.position - b.position);
    const receivable = pos.filter((p) => canReceive(p.status) && p.status !== "received" && !(p.ship_to_location_id && vendorSites.has(p.ship_to_location_id)));
    const backersOf = (p: Po) => (linesByPo.get(p.id) ?? []).reduce((t, l) => t + backersLeft(l), 0);
    const waitsForBackers = (p: Po) => p.kind === "make" && backersOf(p) > 0 && (linesByPo.get(p.id) ?? []).every(hhlDone);
    const open = receivable
      .filter((p) => !waitsForBackers(p))
      .sort((a, b) => rank(a) - rank(b) || (a.expected_arrive ?? "9").localeCompare(b.expected_arrive ?? "9") || a.number.localeCompare(b.number));
    const forBackers = receivable
      .filter(waitsForBackers)
      .map((p) => ({ po: p, units: backersOf(p) }))
      .sort((a, b) => a.po.number.localeCompare(b.po.number, undefined, { numeric: true }));
    const itemById = new Map(items.map((i) => [i.id, i]));
    const vendorById = new Map(vendors.map((v) => [v.id, v]));
    const recent = [...receipts].sort((a, b) => b.received_at.localeCompare(a.received_at));
    return { open, forBackers, linesByPo, itemById, vendorById, poById, recent, locations };
  }, [pos, lines, items, locations, vendors, receipts, makeOrders]);
}

// Shipped POs are the ones arriving; then whatever's in production, then just sent.
const rank = (p: Po) => ({ shipped: 0, in_production: 1, acknowledged: 2, sent: 3 })[p.status as string] ?? 4;

/** The photo to show for a PO line: its snapshot, else the item's current one. */
export const linePhoto = (line: PoLine, item: Item | undefined) => line.photo_url || item?.photo_url || "";

export const SHOPIFY_LABELS: Record<Receipt["shopify_status"], string> = {
  none: "",
  pending: "Posting to Shopify…",
  done: "In Shopify",
  failed: "Shopify refused",
};

// Scan feedback: a short high tone for a hit, a low buzz for a miss (scanner users don't watch the screen).
let audio: AudioContext | null = null;
export function beep(ok: boolean) {
  try {
    audio ??= new AudioContext();
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.type = ok ? "sine" : "square";
    osc.frequency.value = ok ? 1320 : 180;
    gain.gain.value = 0.06;
    osc.connect(gain).connect(audio.destination);
    osc.start();
    osc.stop(audio.currentTime + (ok ? 0.06 : 0.25));
  } catch {}
}

/** "12*CODE", "12xCODE" or "12 CODE": a quantity before the code. */
export function parseScan(text: string): { qty: number; code: string } {
  const m = text.trim().match(/^(\d+(?:\.\d+)?)\s*[*x×]\s*(.+)$/i) ?? text.trim().match(/^(\d{1,4})\s+(\S.*)$/);
  return m ? { qty: Number(m[1]), code: m[2].trim() } : { qty: 1, code: text.trim() };
}
